/**
 * `/agents/:id/configure` — edit-and-bump-version form (RLY-024 AC #5).
 *
 * Submits a PATCH to `/api/v1/agents/:id` which mints a new `agent_versions`
 * row at `current_version + 1`. Shows a "New version (vN) created" toast.
 */

import type { UpdateAgentRequest } from "@simpliwork/api-schema";
import { UpdateAgentRequestSchema } from "@simpliwork/api-schema";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";
import { useAgentDetail, useUpdateAgent } from "@/hooks/use-agents";
import { ApiError } from "@/lib/api-client";

interface ConfigureFormValues {
  name: string;
  description: string;
  model: string;
  system_prompt: string;
  max_tokens: number;
  timeout_seconds: number;
  max_cost_micros: number;
}

export function AgentConfigurePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const agent = useAgentDetail(id);
  const update = useUpdateAgent();

  const form = useForm<ConfigureFormValues>({
    defaultValues: {
      name: "",
      description: "",
      model: "",
      system_prompt: "",
      max_tokens: 4_096,
      timeout_seconds: 60,
      max_cost_micros: 1_000_000,
    },
  });
  const { reset } = form;

  // Once the agent payload lands, prime the form. Done in an effect (not as
  // `defaultValues`) so `useForm` doesn't have to wait for data and the form
  // remains controlled.
  useEffect(() => {
    if (!agent.data) return;
    const cfg = agent.data.current_version_config;
    const cfgConfig = cfg.config as { model?: string; system_prompt?: string };
    reset({
      name: agent.data.name,
      description: agent.data.description ?? "",
      model: cfgConfig.model ?? "",
      system_prompt: cfgConfig.system_prompt ?? "",
      max_tokens: cfg.limits.max_tokens,
      timeout_seconds: cfg.limits.timeout_seconds,
      max_cost_micros: cfg.limits.max_cost_micros,
    });
  }, [agent.data, reset]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!id || !agent.data) return;
    const existing = agent.data.current_version_config.config;
    // Build an UpdateAgentRequest by merging the form into the existing config.
    // The backend (RLY-021) treats omitted fields as "no change", but we want
    // the schema to validate the full discriminated union — so we always send
    // the complete config block.
    const newConfig: UpdateAgentRequest["config"] = {
      ...existing,
      // The form only edits model + system_prompt for now; the rest of the
      // discriminated-union variant carries through unchanged.
      ...("model" in existing ? { model: values.model } : {}),
      ...("system_prompt" in existing ? { system_prompt: values.system_prompt } : {}),
    };
    const body: UpdateAgentRequest = {
      name: values.name,
      description: values.description,
      config: newConfig,
      limits: {
        max_tokens: values.max_tokens,
        timeout_seconds: values.timeout_seconds,
        max_cost_micros: values.max_cost_micros,
      },
    };
    const parsed = UpdateAgentRequestSchema.safeParse(body);
    if (!parsed.success) {
      toast.toast({
        title: "Validation failed",
        description: parsed.error.issues.map((i) => i.message).join("; "),
        variant: "error",
      });
      return;
    }
    try {
      const result = await update.mutateAsync({ agentId: id, body: parsed.data });
      toast.toast({
        title: `New version (v${result.version}) created`,
        variant: "success",
      });
      navigate(`/agents/${id}`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({
        title: "Could not save changes",
        description: message,
        variant: "error",
      });
    }
  });

  if (agent.isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="agent-configure-loading">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (agent.error || !agent.data) {
    return (
      <Card data-testid="agent-configure-error">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">
            Couldn't load agent: {agent.error?.message ?? "unknown"}
          </p>
          <Link to="/agents">
            <Button variant="outline" size="sm" className="mt-4">
              Back to agents
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Configure agent</h1>
        <p className="text-sm text-muted-foreground">
          Saving creates a new version (v{agent.data.current_version + 1}).
        </p>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>{agent.data.name}</CardTitle>
          <CardDescription>Currently on v{agent.data.current_version}.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={onSubmit}
            data-testid="agent-configure-form"
            noValidate
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-edit-name">Name</Label>
              <Input
                id="agent-edit-name"
                data-testid="agent-edit-name-input"
                {...form.register("name")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-edit-description">Description</Label>
              <Input
                id="agent-edit-description"
                data-testid="agent-edit-description-input"
                {...form.register("description")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-edit-model">Model</Label>
              <Input
                id="agent-edit-model"
                data-testid="agent-edit-model-input"
                {...form.register("model")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-edit-system-prompt">System prompt</Label>
              <Textarea
                id="agent-edit-system-prompt"
                rows={6}
                data-testid="agent-edit-system-prompt-input"
                {...form.register("system_prompt")}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="agent-edit-max-tokens">max_tokens</Label>
                <Input
                  id="agent-edit-max-tokens"
                  type="number"
                  min={1}
                  data-testid="agent-edit-max-tokens-input"
                  {...form.register("max_tokens", { valueAsNumber: true })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="agent-edit-timeout">timeout_seconds</Label>
                <Input
                  id="agent-edit-timeout"
                  type="number"
                  min={1}
                  data-testid="agent-edit-timeout-input"
                  {...form.register("timeout_seconds", { valueAsNumber: true })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="agent-edit-max-cost">max_cost_micros</Label>
                <Input
                  id="agent-edit-max-cost"
                  type="number"
                  min={0}
                  data-testid="agent-edit-max-cost-input"
                  {...form.register("max_cost_micros", { valueAsNumber: true })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Link to={`/agents/${agent.data.id}`}>
                <Button type="button" variant="outline" data-testid="agent-configure-cancel">
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                data-testid="agent-configure-submit"
                disabled={update.isPending}
              >
                {update.isPending ? "Saving…" : "Save new version"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default AgentConfigurePage;
